import {
  LucideIcon, Network, Brain, Database, FlaskConical, BarChart3,
  CloudCog, Cloud, GitBranch, Activity, Settings,
} from "lucide-react";

export type RoleDetail = {
  slug: string;
  name: string;
  category: "Data" | "Cloud";
  Icon: LucideIcon;
  headline: string;
  intro: string;
  owns: string[];
  stack: string[];
  whySenior: string;
};

export const ROLES_DETAIL: RoleDetail[] = [
  {
    slug: "data-architect",
    name: "Data Architect",
    category: "Data",
    Icon: Network,
    headline: "Designs the platform everything else depends on.",
    intro: "A senior Data Architect defines how your data is modeled, governed, and moved before a single pipeline gets built. They make the decisions your whole data organization will live with for years.",
    owns: [
      "Data platform architecture and modeling standards",
      "Governance, lineage, and data quality frameworks",
      "Warehouse and lakehouse design decisions",
      "Technology selection across the data stack",
    ],
    stack: ["Snowflake", "Databricks", "dbt", "Kafka", "AWS", "Azure", "GCP"],
    whySenior: "Data modeling mistakes compound for years. A senior architect has already seen which designs collapse at scale and which ones survive a decade of change.",
  },
  {
    slug: "machine-learning-engineer",
    name: "Machine Learning Engineer",
    category: "Data",
    Icon: Brain,
    headline: "Models that survive contact with production.",
    intro: "A senior ML Engineer takes models out of notebooks and into your product: training pipelines, serving infrastructure, and monitoring that catches degradation before your users do.",
    owns: [
      "Training and serving pipelines for production models",
      "Model monitoring, evaluation, and retraining strategy",
      "Experimentation workflows that reach production",
      "Latency and cost optimization for inference",
    ],
    stack: ["Python", "PyTorch", "TensorFlow", "SageMaker", "Vertex AI", "MLflow", "Docker"],
    whySenior: "Most models die between the notebook and production. A senior ML engineer has shipped, monitored, and debugged models that real customers depend on.",
  },
  {
    slug: "data-engineer",
    name: "Data Engineer",
    category: "Data",
    Icon: Database,
    headline: "Reliable pipelines, boring in the best way.",
    intro: "A senior Data Engineer builds the ingestion, transformation, and orchestration layer your analysts and scientists trust blindly. Data arrives complete, on time, and tested.",
    owns: [
      "Batch and streaming ingestion pipelines",
      "Orchestration and dependency management",
      "Warehouse performance and cost optimization",
      "Data contracts, testing, and quality checks",
    ],
    stack: ["Airflow", "Spark", "Kafka", "Snowflake", "dbt", "AWS", "GCP"],
    whySenior: "A senior data engineer has seen a pipeline silently corrupt a warehouse and knows exactly which invariants to check first. That pattern library only comes from production.",
  },
  {
    slug: "data-scientist",
    name: "Data Scientist",
    category: "Data",
    Icon: FlaskConical,
    headline: "From raw data to real decisions.",
    intro: "A senior Data Scientist designs the experiments, builds the models, and tells the story that changes what your business does next. Not dashboards for decoration: analysis that moves metrics.",
    owns: [
      "Experiment design and A/B testing programs",
      "Statistical modeling and forecasting",
      "Exploratory analysis that drives product decisions",
      "Stakeholder-ready communication of findings",
    ],
    stack: ["Python", "SQL", "scikit-learn", "pandas", "Jupyter", "A/B testing"],
    whySenior: "Seniority in data science is knowing which analysis will change a decision and which one just decorates a deck. That judgment is earned, not taught.",
  },
  {
    slug: "analytics-engineer",
    name: "Analytics Engineer",
    category: "Data",
    Icon: BarChart3,
    headline: "One number everyone trusts.",
    intro: "A senior Analytics Engineer owns the semantic layer: the dbt models, metric definitions, and dashboards your whole company relies on. When finance and product open the same chart, they see the same number.",
    owns: [
      "Semantic layer and dbt model ownership",
      "Company-wide metric definitions",
      "BI performance and dashboard reliability",
      "Data documentation and discoverability",
    ],
    stack: ["dbt", "Snowflake", "BigQuery", "Looker", "Tableau", "SQL"],
    whySenior: "Defining metrics sounds easy until sales, finance, and product disagree. A senior analytics engineer has navigated that politics and shipped definitions that stick.",
  },
  {
    slug: "cloud-solutions-architect",
    name: "Cloud / Solutions Architect",
    category: "Cloud",
    Icon: CloudCog,
    headline: "Architecture that survives scale and audits.",
    intro: "A senior Cloud Architect designs the landing zones, account strategy, and disaster recovery posture your infrastructure grows into. Resilient, cost-aware, and audit-ready from day one.",
    owns: [
      "Landing zones and multi-account strategy",
      "High availability and disaster recovery design",
      "Cost architecture and commitment planning",
      "Security baselines and compliance posture",
    ],
    stack: ["AWS", "Azure", "GCP", "Terraform", "Kubernetes", "Networking"],
    whySenior: "Every senior cloud architect carries scars from an incident caused by an innocent-looking design decision. You are hiring that pattern library.",
  },
  {
    slug: "cloud-engineer",
    name: "Cloud Engineer",
    category: "Cloud",
    Icon: Cloud,
    headline: "Infrastructure as code, every day.",
    intro: "A senior Cloud Engineer builds and operates your cloud foundation: IaC modules, networking, IAM, and environments that your product teams deploy onto with confidence.",
    owns: [
      "Infrastructure as Code modules and standards",
      "Networking, IAM, and security configuration",
      "Environment provisioning and drift control",
      "Cost hygiene and resource governance",
    ],
    stack: ["Terraform", "AWS", "Azure", "GCP", "Kubernetes", "GitHub Actions"],
    whySenior: "A misconfigured cloud bill or an over-privileged IAM role outlives the person who created it. Senior cloud engineers leave clean foundations, not landmines.",
  },
  {
    slug: "devops-engineer",
    name: "DevOps Engineer",
    category: "Cloud",
    Icon: GitBranch,
    headline: "Ship faster without breaking things.",
    intro: "A senior DevOps Engineer owns your CI/CD pipelines, automation, and developer experience. Releases go from stressful events to boring, safe, weekly routines.",
    owns: [
      "CI/CD pipeline design and maintenance",
      "Build and release automation",
      "Developer experience and internal tooling",
      "Secrets, config, and environment management",
    ],
    stack: ["GitHub Actions", "GitLab CI", "Docker", "Kubernetes", "ArgoCD", "Terraform"],
    whySenior: "Release processes designed by juniors break under load at the worst moment. A senior DevOps engineer designs for the bad day, not the demo.",
  },
  {
    slug: "site-reliability-engineer",
    name: "Site Reliability Engineer",
    category: "Cloud",
    Icon: Activity,
    headline: "Reliability as an engineering discipline.",
    intro: "A senior SRE keeps production honest: SLOs, error budgets, observability, and incident response that turns 3am pages into calm, practiced routines.",
    owns: [
      "SLOs, SLIs, and error budget policy",
      "Observability: metrics, logs, traces, alerts",
      "Incident response and blameless postmortems",
      "Capacity planning and load testing",
    ],
    stack: ["Prometheus", "Grafana", "Datadog", "PagerDuty", "Kubernetes", "OpenTelemetry"],
    whySenior: "Calm incident response only comes from having been paged at 3am for years. A senior SRE has already made the mistakes your system cannot afford.",
  },
  {
    slug: "mlops-engineer",
    name: "MLOps Engineer",
    category: "Cloud",
    Icon: Settings,
    headline: "The ML lifecycle, production-grade.",
    intro: "A senior MLOps Engineer owns everything between a trained model and a reliable product feature: pipelines, versioning, deployment automation, and drift detection.",
    owns: [
      "Training pipeline automation",
      "Model registry, versioning, and governance",
      "Deployment and rollback automation for models",
      "Drift detection and retraining triggers",
    ],
    stack: ["MLflow", "Kubeflow", "SageMaker", "Vertex AI", "Docker", "Airflow"],
    whySenior: "Models rot silently. A senior MLOps engineer has watched drift destroy a product metric and builds the monitoring that catches it next time.",
  },
];

export const roleSlug = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
